import { useState, useEffect } from 'react'
import { adminService } from 'core/services/adminService'
import { formatNumbers } from 'core/helpers/format-numbers'
import { toast } from 'react-toastify'

import c from './assets/sass/task-edit.module.scss'

const TASK_TYPES = [
    { value: 'DAILY_LOGIN', label: '📅 Ежедневный вход' },
    { value: 'REFER_FRIENDS', label: '👥 Пригласить друзей' },
    { value: 'PLAY_MINES', label: '💣 Сыграть в Мины' },
    { value: 'PLAY_BATTLE', label: '⚔️ Сыграть в Битву' },
    { value: 'OPEN_CASES', label: '🎁 Открыть кейс' },
    { value: 'TAP_ENERGY', label: '⚡ Тапнуть' },
    { value: 'TELEGRAM_CHANNEL', label: '📢 Подписка на Telegram канал' },
    { value: 'EXTERNAL_LINK', label: '🔗 Перейти по внешней ссылке' },
]

export const TaskEdit = ({ task, onSave, onCancel }) => {
    const [form, setForm] = useState({
        type: task?.type || 'EXTERNAL_LINK',
        title: task?.title || '',
        description: task?.description || '',
        reward: String(task?.reward || '0'),
        energyReward: Number(task?.energyReward) || 0,
        url: task?.url || '',
        imageUrl: task?.imageUrl || '',
        isActive: task?.isActive ?? true,
        requirement: task?.requirement || {},
        maxCompletions: Number(task?.maxCompletions) || 1,
        orderIndex: Number(task?.orderIndex) || 0,
    })
    const [loading, setLoading] = useState(false)
    const [casesList, setCasesList] = useState([])
    const [channelVerified, setChannelVerified] = useState(null)
    const [verifying, setVerifying] = useState(false)

    useEffect(() => {
        if (form.type === 'OPEN_CASES') {
            loadCases()
        }
    }, [form.type])

    const loadCases = async () => {
        try {
            const data = await adminService.getTaskCasesList()
            setCasesList(data.cases || [])
        } catch (error) {
            console.error('Failed to load cases:', error)
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        try {
            setLoading(true)
            if (task) {
                await adminService.updateTask(task.id, form)
                toast('Задание обновлено', { type: 'success' })
            } else {
                await adminService.createTask(form)
                toast('Задание создано', { type: 'success' })
            }
            onSave()
        } catch (error) {
            toast('Ошибка: ' + error.message, { type: 'error' })
        } finally {
            setLoading(false)
        }
    }

    const handleVerifyChannel = async () => {
        const channelId = form.requirement?.channelId
        if (!channelId) {
            alert('Введите ID канала')
            return
        }

        try {
            setVerifying(true)
            const result = await adminService.verifyBotInChannel(channelId)

            if (result.success && result.isAdmin) {
                setChannelVerified(true)
                toast('✅ Бот является администратором канала!', {
                    type: 'success',
                })
            } else if (result.success && !result.isAdmin) {
                setChannelVerified(false)
                toast('❌ Бот НЕ является администратором канала', {
                    type: 'error',
                })
            } else {
                setChannelVerified(false)
                toast(
                    '❌ Ошибка: ' +
                        (result.error || 'Не удалось проверить канал'),
                    { type: 'error' }
                )
            }
        } catch (error) {
            setChannelVerified(false)
            toast('Ошибка проверки: ' + error.message, { type: 'error' })
        } finally {
            setVerifying(false)
        }
    }

    return (
        <div className={c.component}>
            <h3>{task ? 'Редактирование задания' : 'Создание задания'}</h3>
            <form onSubmit={handleSubmit} className={c.form}>
                <label>
                    Тип задания:
                    <select
                        value={form.type}
                        onChange={(e) =>
                            setForm({ ...form, type: e.target.value })
                        }
                    >
                        {TASK_TYPES.map((type) => (
                            <option key={type.value} value={type.value}>
                                {type.label}
                            </option>
                        ))}
                    </select>
                </label>
                <label>
                    Название:
                    <input
                        type="text"
                        value={form.title || ''}
                        onChange={(e) =>
                            setForm({ ...form, title: e.target.value })
                        }
                        required
                    />
                </label>
                <label>
                    Описание:
                    <textarea
                        value={form.description || ''}
                        onChange={(e) =>
                            setForm({ ...form, description: e.target.value })
                        }
                        required
                    />
                </label>
                <label>
                    Награда (монеты):
                    <input
                        type="number"
                        value={form.reward || '0'}
                        onChange={(e) =>
                            setForm({ ...form, reward: e.target.value || '0' })
                        }
                    />
                </label>
                <label>
                    Награда (энергия):
                    <input
                        type="number"
                        value={form.energyReward || 0}
                        onChange={(e) =>
                            setForm({
                                ...form,
                                energyReward: parseInt(e.target.value) || 0,
                            })
                        }
                    />
                </label>
                {form.type === 'EXTERNAL_LINK' && (
                    <>
                        <label>
                            URL (ссылка):
                            <input
                                type="text"
                                value={form.url || ''}
                                onChange={(e) =>
                                    setForm({ ...form, url: e.target.value })
                                }
                                placeholder="https://example.com"
                                required
                            />
                        </label>
                        <label>
                            Время ожидания после перехода (секунд):
                            <input
                                type="number"
                                min="1"
                                value={form.requirement?.waitSeconds || 5}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        requirement: {
                                            ...form.requirement,
                                            waitSeconds:
                                                parseInt(e.target.value) || 5,
                                        },
                                    })
                                }
                            />
                            <small
                                style={{
                                    color: 'rgba(255,255,255,0.5)',
                                    fontSize: '12px',
                                    display: 'block',
                                    marginTop: '4px',
                                }}
                            >
                                Юзер должен подождать после перехода по ссылке
                            </small>
                        </label>
                    </>
                )}
                {form.type === 'TELEGRAM_CHANNEL' && (
                    <>
                        <label>
                            URL канала (для открытия юзеру):
                            <input
                                type="text"
                                value={form.url || ''}
                                onChange={(e) =>
                                    setForm({ ...form, url: e.target.value })
                                }
                                placeholder="https://t.me/your_channel"
                                required
                            />
                            <small
                                style={{
                                    color: 'rgba(255,255,255,0.5)',
                                    fontSize: '12px',
                                    display: 'block',
                                    marginTop: '4px',
                                }}
                            >
                                Ссылка на канал, которая откроется при клике на
                                задание
                            </small>
                        </label>
                        <label>
                            ID канала для проверки подписки (например: @channel
                            или -100123456789):
                            <input
                                type="text"
                                value={form.requirement?.channelId || ''}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        requirement: {
                                            ...form.requirement,
                                            channelId: e.target.value,
                                        },
                                    })
                                }
                                placeholder="@your_channel или -100123456789"
                                required
                            />
                            <small
                                style={{
                                    color: 'rgba(255,255,255,0.5)',
                                    fontSize: '12px',
                                    display: 'block',
                                    marginTop: '4px',
                                }}
                            >
                                ID канала для проверки через Telegram API (бот
                                должен быть админом)
                            </small>
                        </label>
                        <button
                            type="button"
                            onClick={handleVerifyChannel}
                            disabled={verifying || !form.requirement?.channelId}
                            style={{ marginTop: '8px' }}
                        >
                            {verifying
                                ? 'Проверка...'
                                : 'Проверить доступ бота'}
                        </button>
                        {channelVerified !== null && (
                            <p
                                style={{
                                    color: channelVerified
                                        ? '#4CAF50'
                                        : '#FF4444',
                                    margin: '8px 0',
                                }}
                            >
                                {channelVerified
                                    ? '✅ Бот администратор канала - проверка подписки работает!'
                                    : '⚠️ Бот НЕ администратор канала - проверка подписки не сработает!'}
                            </p>
                        )}
                    </>
                )}
                {form.type === 'DAILY_LOGIN' && (
                    <label>
                        Количество дней подряд:
                        <input
                            type="number"
                            min="1"
                            value={form.requirement?.days || 1}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    requirement: {
                                        ...form.requirement,
                                        days: parseInt(e.target.value) || 1,
                                    },
                                })
                            }
                        />
                        <small
                            style={{
                                color: 'rgba(255,255,255,0.5)',
                                fontSize: '12px',
                                display: 'block',
                                marginTop: '4px',
                            }}
                        >
                            Юзер должен зайти {form.requirement?.days || 1} дней
                            подряд
                        </small>
                    </label>
                )}
                {form.type === 'REFER_FRIENDS' && (
                    <label>
                        Количество друзей:
                        <input
                            type="number"
                            min="1"
                            value={form.requirement?.count || 1}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    requirement: {
                                        ...form.requirement,
                                        count: parseInt(e.target.value) || 1,
                                    },
                                })
                            }
                        />
                    </label>
                )}
                {form.type === 'TAP_ENERGY' && (
                    <label>
                        Количество тапов:
                        <input
                            type="number"
                            min="1"
                            value={form.requirement?.tapsCount || 100}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    requirement: {
                                        ...form.requirement,
                                        tapsCount:
                                            parseInt(e.target.value) || 100,
                                    },
                                })
                            }
                        />
                        <small
                            style={{
                                color: 'rgba(255,255,255,0.5)',
                                fontSize: '12px',
                                display: 'block',
                                marginTop: '4px',
                            }}
                        >
                            Засчитываются только тапы по роботу с начислением
                            баланса
                        </small>
                    </label>
                )}
                {form.type === 'PLAY_MINES' && (
                    <>
                        <label>
                            Количество игр:
                            <input
                                type="number"
                                min="1"
                                value={form.requirement?.gamesCount || 1}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        requirement: {
                                            ...form.requirement,
                                            gamesCount:
                                                parseInt(e.target.value) || 1,
                                        },
                                    })
                                }
                            />
                        </label>
                        <label className={c.checkbox}>
                            <input
                                type="checkbox"
                                checked={form.requirement?.mustWin !== false}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        requirement: {
                                            ...form.requirement,
                                            mustWin: e.target.checked,
                                        },
                                    })
                                }
                            />
                            Только выигранные игры
                        </label>
                        {form.requirement?.mustWin !== false && (
                            <label>
                                Минимальная сумма выигрыша (опционально):
                                <input
                                    type="number"
                                    min="0"
                                    value={form.requirement?.minWinAmount || ''}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            requirement: {
                                                ...form.requirement,
                                                minWinAmount: e.target.value
                                                    ? parseInt(e.target.value)
                                                    : undefined,
                                            },
                                        })
                                    }
                                    placeholder="Оставь пустым для любого выигрыша"
                                />
                                <small
                                    style={{
                                        color: 'rgba(255,255,255,0.5)',
                                        fontSize: '12px',
                                        display: 'block',
                                        marginTop: '4px',
                                    }}
                                >
                                    Например: 5000 = выигрыш минимум 5000 монет
                                </small>
                            </label>
                        )}
                    </>
                )}
                {form.type === 'PLAY_BATTLE' && (
                    <>
                        <label>
                            Количество битв:
                            <input
                                type="number"
                                min="1"
                                value={form.requirement?.battlesCount || 1}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        requirement: {
                                            ...form.requirement,
                                            battlesCount:
                                                parseInt(e.target.value) || 1,
                                        },
                                    })
                                }
                            />
                        </label>
                        <label className={c.checkbox}>
                            <input
                                type="checkbox"
                                checked={form.requirement?.mustWin || false}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        requirement: {
                                            ...form.requirement,
                                            mustWin: e.target.checked,
                                        },
                                    })
                                }
                            />
                            Только выигранные битвы
                        </label>
                        <label className={c.checkbox}>
                            <input
                                type="checkbox"
                                checked={
                                    form.requirement?.differentOpponents ||
                                    false
                                }
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        requirement: {
                                            ...form.requirement,
                                            differentOpponents:
                                                e.target.checked,
                                        },
                                    })
                                }
                            />
                            С разными противниками
                            <small
                                style={{
                                    color: 'rgba(255,255,255,0.5)',
                                    fontSize: '12px',
                                    display: 'block',
                                    marginTop: '4px',
                                }}
                            >
                                Засчитываются только битвы с уникальными
                                игроками
                            </small>
                        </label>
                    </>
                )}
                {form.type === 'OPEN_CASES' && (
                    <>
                        <label>
                            Количество открытий:
                            <input
                                type="number"
                                min="1"
                                value={form.requirement?.casesCount || 1}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        requirement: {
                                            ...form.requirement,
                                            casesCount:
                                                parseInt(e.target.value) || 1,
                                        },
                                    })
                                }
                            />
                        </label>
                        {casesList.length > 0 && (
                            <label>
                                Конкретный кейс (опционально):
                                <select
                                    value={form.requirement?.caseId || ''}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            requirement: {
                                                ...form.requirement,
                                                caseId:
                                                    e.target.value || undefined,
                                            },
                                        })
                                    }
                                >
                                    <option value="">Любой кейс</option>
                                    {casesList.map((cs) => (
                                        <option key={cs.id} value={cs.id}>
                                            {cs.name} (
                                            {formatNumbers(parseInt(cs.price))}{' '}
                                            монет)
                                        </option>
                                    ))}
                                </select>
                                <small
                                    style={{
                                        color: 'rgba(255,255,255,0.5)',
                                        fontSize: '12px',
                                        display: 'block',
                                        marginTop: '4px',
                                    }}
                                >
                                    Если выбран - засчитываются только покупки
                                    этого кейса
                                </small>
                            </label>
                        )}
                    </>
                )}
                <label>
                    URL картинки:
                    <input
                        type="text"
                        value={form.imageUrl || ''}
                        onChange={(e) =>
                            setForm({ ...form, imageUrl: e.target.value })
                        }
                        placeholder="/images/task-icon.png"
                    />
                </label>
                <label>
                    Макс. выполнений:
                    <input
                        type="number"
                        min="1"
                        value={form.maxCompletions || 1}
                        onChange={(e) =>
                            setForm({
                                ...form,
                                maxCompletions: parseInt(e.target.value) || 1,
                            })
                        }
                    />
                </label>
                <label>
                    Порядок (сортировка):
                    <input
                        type="number"
                        value={form.orderIndex || 0}
                        onChange={(e) =>
                            setForm({
                                ...form,
                                orderIndex: parseInt(e.target.value) || 0,
                            })
                        }
                    />
                </label>
                <label className={c.checkbox}>
                    <input
                        type="checkbox"
                        checked={form.isActive}
                        onChange={(e) =>
                            setForm({ ...form, isActive: e.target.checked })
                        }
                    />
                    Активно
                </label>
                <div className={c.buttons}>
                    <button type="submit" disabled={loading}>
                        Сохранить
                    </button>
                    <button type="button" onClick={onCancel}>
                        Отмена
                    </button>
                </div>
            </form>
        </div>
    )
}
